// test comment for ts_test
import { configure, reaction } from "mobx";
import React from "react";
import ReactDOM from "react-dom/client";

import "setimmediate";
import "uuid";

import { MetricReporter } from "@formant/core/client/MetricReporter";
import { BaseClient } from "@formant/core/common/BaseClient";
import {
    app,
    buildTag,
    enableSentry,
    gitCommit,
    jestWorkerId,
    logApiEndpoint,
    namespace,
    sentryDsn,
} from "@formant/core/common/config";
import { notNull } from "@formant/core/common/defined";
import { delay } from "@formant/core/common/delay";
import { tagRestricted } from "@formant/core/common/errorConstants";
import { forever } from "@formant/core/common/forever";
import { fork } from "@formant/core/common/fork";
import { FormantBaseClient } from "@formant/core/common/FormantBaseClient";
import { logger } from "@formant/core/common/logger";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { stats } from "@formant/core/common/stats";
import { duration } from "@formant/core/model/duration";

import "@formant/rectangle-sdk/src/contexts/FormantContext";

import { animate } from "@formant/ui/utils/animate";

import "./analytics/init";

import { authClient } from "./clients";
import { App } from "./component/App";
import { FeatureUnavailablePageError } from "./component/FeatureUnavailablePageError";
import { autoLoginEmail } from "./config";
import { logStats } from "./logStats";
import { authStore } from "./stores";
import { setupSentry } from "./utils/setupSentry";

import "@formant/ui-kit/static/index.css";
import "@formant/ui/style/uikit.scss";

configure({ isolateGlobalState: true });

BaseClient.waitForConnectivity = async () => {
    while (navigator.onLine === false) {
        await delay(duration.second);
    }
};

FormantBaseClient.onResponseError = async error => {
    await delay(duration.second);
    if (
        error.statusCode === 401 &&
        !error.url.endsWith("login") &&
        !error.url.endsWith("logs") &&
        !error.url.endsWith("metrics")
    ) {
        authStore.logout();
        window.location.reload();
        await forever();
    }

    const tagError =
        error.statusCode === 422 &&
        error.validationErrors.tags?.includes(tagRestricted);

    const tagsRecentlyChanged =
        tagError &&
        (error.url.endsWith("login") ||
            error.url.includes("roles/by-user/") ||
            error.url.includes("admin/users/"));

    if (tagsRecentlyChanged) {
        await authStore.refreshLogin();
        return;
    }

    if (
        (error.statusCode === 403 &&
            !error.url.includes("feedback.formant.io")) ||
        tagError
    ) {
        // user role may have been updated, logout to forcefully re-sync
        authStore.logout();
        throw new FeatureUnavailablePageError(error);
    }
};

const inUnitTest = !!jestWorkerId;
if (logApiEndpoint && !inUnitTest) {
    const getToken = () => authStore.token();
    const metrics = new MetricReporter(logApiEndpoint, getToken, false);
    stats.registerStatsReporter(metrics);
    reaction(
        () => !!authStore.currentToken,
        isLoggedIn => {
            if (isLoggedIn) {
                logger.info("Metrics reporting enabled");
                metrics.active = true;
            } else {
                logger.info("Metrics reporting disabled");
                metrics.active = false;
            }
        },
        { name: "app/metrics/activator", fireImmediately: true },
    );
}

if (enableSentry) {
    setupSentry(
        sentryDsn,
        { environment: namespace, release: gitCommit },
        { app, version: buildTag },
    );
}

async function start() {
    const { userAgent } = navigator;
    logger.info("Started", { buildTag, userAgent });

    logStats();

    try {
        if (autoLoginEmail) {
            try {
                await authStore.login(autoLoginEmail, autoLoginEmail);
            } catch (error) {
                narrowError(error, ResponseError, _ => _.statusCode === 401);
                await authClient.adminSignup({
                    email: autoLoginEmail,
                    firstName: "Admin",
                    lastName: "Admin",
                });
                await authStore.login(autoLoginEmail, autoLoginEmail);
            }
        } else {
            await authStore.refreshLogin();
        }
    } catch (error: any) {
        throw new Error("Failed to login");
    }

    const root = ReactDOM.createRoot(notNull(document.getElementById("root")));

    root.render(React.createElement(App));

    await removeLoadingIndicator();
}

async function removeLoadingIndicator() {
    const loading = document.querySelector("#loading");
    if (loading && loading.parentNode) {
        loading.classList.add("hidden");
        await animate();
        loading.parentNode.removeChild(loading);
    }
}

fork(start());
